/**
 * Markdown field component
 * Monaco editor with search/replace, syntax highlighting, fullscreen editing,
 * and localStorage draft auto-save.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Editor, { OnMount } from '@monaco-editor/react';
import { Maximize2, Minimize2, RotateCcw, Save, Loader2 } from 'lucide-react';
import { FieldSchema, ValidationResult, getHelpText } from '@/config/fieldConfig';
import { fetchSystemDefault } from '@/lib/api/systemDefaults';
import { AIRewriteToolbar } from './AIRewriteToolbar';
import { useI18n } from '@/i18n';
import type * as Monaco from 'monaco-editor';

// ---------------------------------------------------------------------------
// Draft persistence helpers
// ---------------------------------------------------------------------------

/** Build a localStorage key scoped to current page + field name */
const getDraftKey = (name: string): string => {
  // Use hash portion of URL (e.g. "#/agent-config/dora") to scope per agent
  const scope = window.location.hash || window.location.pathname;
  return `markdown-draft:${scope}:${name}`;
};

const saveDraft = (name: string, content: string) => {
  try {
    localStorage.setItem(getDraftKey(name), JSON.stringify({
      content,
      savedAt: Date.now(),
    }));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
};

const loadDraft = (name: string): { content: string; savedAt: number } | null => {
  try {
    const raw = localStorage.getItem(getDraftKey(name));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const clearDraft = (name: string) => {
  try {
    localStorage.removeItem(getDraftKey(name));
  } catch {
    // ignore
  }
};

const formatTime = (ts: number): string => {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ---------------------------------------------------------------------------
// Types & shared config
// ---------------------------------------------------------------------------

export interface MarkdownFieldProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
}

export const getEditorOptions = (readOnly: boolean) => ({
  readOnly,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  fontSize: 13,
  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  wordWrap: 'on' as const,
  wrappingStrategy: 'advanced' as const,
  padding: { top: 8, bottom: 8 },
  overviewRulerLanes: 0,
  hideCursorInOverviewRuler: true,
  renderLineHighlight: 'none' as const,
  scrollbar: {
    vertical: 'auto' as const,
    horizontal: 'hidden' as const,
    verticalScrollbarSize: 8,
  },
  find: {
    addExtraSpaceOnTop: false,
    seedSearchStringFromSelection: 'selection' as const,
  },
});

// ---------------------------------------------------------------------------
// Draft recovery banner
// ---------------------------------------------------------------------------

const DraftBanner: React.FC<{
  savedAt: number;
  onRestore: () => void;
  onDiscard: () => void;
}> = ({ savedAt, onRestore, onDiscard }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 mb-1 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
    <Save size={13} className="shrink-0" />
    <span className="flex-1">
      发现未保存的草稿 ({formatTime(savedAt)})
    </span>
    <button
      type="button"
      onClick={onRestore}
      className="px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 font-medium"
    >
      恢复草稿
    </button>
    <button
      type="button"
      onClick={onDiscard}
      className="px-2 py-0.5 rounded border border-amber-300 hover:bg-amber-100 font-medium"
    >
      丢弃
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Fullscreen editor (portal)
// ---------------------------------------------------------------------------

const FullscreenEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  label: string;
  onClose: () => void;
  showPreview: boolean;
  onTogglePreview: () => void;
  renderMarkdown: (text: string) => string;
  onReloadServer: () => void;
  hasDraft: boolean;
}> = ({ value, onChange, readOnly, label, onClose, showPreview, onTogglePreview, renderMarkdown, onReloadServer, hasDraft }) => {
  const [fsEditor, setFsEditor] = useState<Monaco.editor.IStandaloneCodeEditor | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleMount: OnMount = (editor) => {
    editor.updateOptions({ wordWrap: 'on' });
    editor.focus();
    setFsEditor(editor);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-800">{label}</h3>
        <div className="flex items-center gap-3">
          {hasDraft && (
            <span className="text-xs text-green-600">草稿已暂存</span>
          )}
          <span className="text-xs text-gray-400">⌘F 搜索 · ⌘H 替换 · 选中文字可 AI 改写</span>
          <button
            type="button"
            onClick={onReloadServer}
            className="p-1 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded"
            title="丢弃修改，恢复为已保存版本"
          >
            <RotateCcw size={14} />
          </button>
          <button
            type="button"
            onClick={onTogglePreview}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded"
            title="退出全屏 (Esc)"
          >
            <Minimize2 size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {!showPreview ? (
          <Editor
            height="100%"
            defaultLanguage="markdown"
            value={value || ''}
            onChange={(val) => onChange(val || '')}
            onMount={handleMount}
            options={{
              ...getEditorOptions(readOnly),
              lineNumbers: 'on',
              minimap: { enabled: true },
              padding: { top: 12, bottom: 12 },
              fontSize: 14,
            }}
            theme="vs"
          />
        ) : (
          <div
            className="h-full overflow-auto px-6 py-4 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(value || '') }}
          />
        )}
      </div>

      {/* AI Rewrite toolbar for fullscreen editor */}
      {fsEditor && !readOnly && !showPreview && (
        <AIRewriteToolbar editor={fsEditor} fullText={value || ''} />
      )}
    </div>,
    document.body,
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const MarkdownField: React.FC<MarkdownFieldProps> = ({
  name,
  value,
  onChange,
  schema,
  error,
  disabled = false,
}) => {
  const { t } = useI18n();
  const [showPreview, setShowPreview] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [inlineEditor, setInlineEditor] = useState<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const [isLoadingDefault, setIsLoadingDefault] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const label = typeof schema.label === 'function' ? schema.label() : schema.label;
  const help = getHelpText(schema.help);
  const rows = schema.rows || 10;
  const editorHeight = `${rows * 22}px`;
  const readOnly = disabled || !!schema.readonly;

  // ------ Draft state ------
  // `serverValue` captures the value prop at mount (= what came from DB).
  // We use it to detect whether a localStorage draft differs from the server.
  const serverValueRef = useRef(value);
  const [pendingDraft, setPendingDraft] = useState<{ content: string; savedAt: number } | null>(null);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // On first mount, check for a saved draft
  useEffect(() => {
    const draft = loadDraft(name);
    if (draft && draft.content !== value) {
      setPendingDraft(draft);
    } else {
      // Draft matches server value — clean it up
      clearDraft(name);
    }
    serverValueRef.current = value;
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When parent saves successfully, the prop value will be updated to match current content.
  // If prop value changes externally (not from our onChange), update serverValueRef.
  const lastOnChangeValueRef = useRef(value);
  useEffect(() => {
    // External update (e.g. form save callback, or page navigation) — not from our own onChange
    if (value !== lastOnChangeValueRef.current) {
      serverValueRef.current = value;
      // If saved value now matches draft, no need to keep draft
      const draft = loadDraft(name);
      if (!draft || draft.content === value) {
        clearDraft(name);
        setPendingDraft(null);
      }
    }
    lastOnChangeValueRef.current = value;
  }, [value, name]);

  // Debounced save to localStorage on every edit
  const handleChange = useCallback((newValue: string) => {
    lastOnChangeValueRef.current = newValue;
    onChange(newValue);
    // Dismiss the recovery banner once user starts editing
    setPendingDraft(null);

    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      if (newValue !== serverValueRef.current) {
        saveDraft(name, newValue);
      } else {
        clearDraft(name);
      }
    }, 800);
  }, [onChange, name]);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
  }, []);

  const handleRestoreDraft = useCallback(() => {
    if (pendingDraft) {
      onChange(pendingDraft.content);
      lastOnChangeValueRef.current = pendingDraft.content;
      setPendingDraft(null);
    }
  }, [pendingDraft, onChange]);

  const handleDiscardDraft = useCallback(() => {
    if (!window.confirm('确定丢弃草稿？将使用服务器上的已保存版本。')) return;
    clearDraft(name);
    setPendingDraft(null);
  }, [name]);

  /** Reload from server — discard all local edits */
  const handleReloadServer = useCallback(() => {
    if (!window.confirm('确定丢弃所有修改？将恢复为上次保存的版本。')) return;
    clearDraft(name);
    setPendingDraft(null);
    onChange(serverValueRef.current);
    lastOnChangeValueRef.current = serverValueRef.current;
  }, [name, onChange]);

  // ------ Reset to system default ------
  const handleResetToSystemDefault = useCallback(async () => {
    setIsLoadingDefault(true);
    setResetError(null);
    try {
      const result = await fetchSystemDefault(name);
      if (result.success && result.value !== undefined) {
        const newValue = typeof result.value === 'object'
          ? JSON.stringify(result.value, null, 2)
          : String(result.value);
        onChange(newValue);
        lastOnChangeValueRef.current = newValue;
        clearDraft(name);
        setPendingDraft(null);
      } else {
        setResetError(result.error || 'Failed to load system default');
      }
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to load system default');
    } finally {
      setIsLoadingDefault(false);
    }
  }, [name, onChange]);

  // ------ Rendering helpers ------

  const borderColor = error && !error.isWarning
    ? 'border-red-500'
    : error?.isWarning
    ? 'border-yellow-500'
    : 'border-gray-300';

  const handleEditorMount: OnMount = useCallback((editor) => {
    editor.updateOptions({ wordWrap: 'on' });
    setInlineEditor(editor);
  }, []);

  const renderMarkdown = useCallback((text: string): string => {
    if (!text) return '';
    return text
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-2 rounded my-2 overflow-x-auto"><code>$1</code></pre>')
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
      .replace(/^\* (.*$)/gim, '<li class="ml-4">$1</li>')
      .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
      .replace(/\n\n/g, '</p><p class="my-2">')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:underline" target="_blank">$1</a>');
  }, []);

  const hasDraft = !!loadDraft(name);

  return (
    <div className="field-wrapper">
      {/* Draft recovery banner */}
      {pendingDraft && (
        <DraftBanner
          savedAt={pendingDraft.savedAt}
          onRestore={handleRestoreDraft}
          onDiscard={handleDiscardDraft}
        />
      )}

      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <label htmlFor={`field-${name}`} className="block text-sm font-medium text-gray-700">
            {label}
            {schema.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {schema.hasResetButton && !disabled && !readOnly && (
            <button
              type="button"
              onClick={handleResetToSystemDefault}
              disabled={isLoadingDefault}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 transition-colors"
              title={t('actions.resetToSystemDefault')}
            >
              {isLoadingDefault ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RotateCcw className="w-3 h-3" />
              )}
              {t('actions.resetToSystemDefault')}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!showPreview && (
            <span className="text-xs text-gray-400">⌘F 搜索 · ⌘H 替换 · 选中文字可 AI 改写</span>
          )}
          <button
            type="button"
            onClick={handleReloadServer}
            className="p-0.5 text-gray-400 hover:text-orange-600 rounded"
            title="丢弃修改，恢复为已保存版本"
          >
            <RotateCcw size={13} />
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={() => setIsFullscreen(true)}
            className="p-0.5 text-gray-400 hover:text-gray-700 rounded"
            title="全屏编辑"
          >
            <Maximize2 size={14} />
          </button>
        </div>
      </div>

      <div className="editor-container">
        {!showPreview ? (
          <div className={`rounded-lg border overflow-hidden ${borderColor}`}>
            <Editor
              height={editorHeight}
              defaultLanguage="markdown"
              value={value || ''}
              onChange={(val) => handleChange(val || '')}
              onMount={handleEditorMount}
              options={{
                ...getEditorOptions(readOnly),
                lineNumbers: 'off',
              }}
              theme="vs"
            />
          </div>
        ) : (
          <div
            className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 min-h-[200px] overflow-auto prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(value || '') }}
          />
        )}
      </div>

      {error && (
        <div className={`text-xs mt-1 ${error.isWarning ? 'text-yellow-600' : 'text-red-600'}`}>
          {error.message}
        </div>
      )}

      {resetError && (
        <div className="text-xs mt-1 text-red-600">
          {resetError}
        </div>
      )}

      {help && !error && !resetError && (
        <div className="text-xs text-gray-500 mt-1">
          {help}
        </div>
      )}

      {/* AI Rewrite toolbar for inline editor */}
      {inlineEditor && !readOnly && !showPreview && (
        <AIRewriteToolbar editor={inlineEditor} fullText={value || ''} />
      )}

      {isFullscreen && (
        <FullscreenEditor
          value={value}
          onChange={handleChange}
          readOnly={readOnly}
          label={label}
          onClose={() => setIsFullscreen(false)}
          showPreview={showPreview}
          onTogglePreview={() => setShowPreview(!showPreview)}
          renderMarkdown={renderMarkdown}
          onReloadServer={handleReloadServer}
          hasDraft={hasDraft}
        />
      )}
    </div>
  );
};

export default MarkdownField;
